import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Settings,
  Share2,
  Plus,
  Download,
  Upload,
  Filter,
  Search,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Save,
  Layers,
  ArrowUpDown,
} from "lucide-react";
import useFinance from "../../../hooks/useFinance";
import DataTable from "../DataGrid/DataTable";
import TablePermissions from "../PermissionManagement/TablePermissions";
import ShareTableModal from "../Modals/ShareTableModal";
import ColumnModal from "../Modals/ColumnModal";
import RowModal from "../Modals/RowModal";
import SaveTemplateModal from "../Modals/SaveTemplateModal";
import ColumnReorderModal from "../Modals/ColumnReorderModal";
import LoadingSpinner from "../Common/LoadingSpinner";
import PermissionBadge from "../Common/PermissionBadge";
import MainHeader from "../../../MainHeader";

export default function TableView() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const {
    fetchTableDetail,
    fetchRows,
    fetchAggregations,
    createRow,
    updateRow,
    deleteRow,
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    createAggregation,
    deleteAggregation,
    createCustomTemplate,
  } = useFinance();

  // States
  const [loading, setLoading] = useState(true);
  const [table, setTable] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [permission, setPermission] = useState(null);
  const [statistics, setStatistics] = useState({});

  // UI States
  const [showPermissions, setShowPermissions] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showRowModal, setShowRowModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    loadData();
  }, [tableId, refreshKey]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load table details
      const tableResult = await fetchTableDetail(tableId);
      if (tableResult.ok) {
        setTable(tableResult.table);
        setColumns(tableResult.columns);
        setPermission(tableResult.permission);
        setStatistics(tableResult.statistics);
      } else if (tableResult.denied) {
        window.showToast?.("테이블 접근 권한이 없습니다.", "error");
        navigate("/finance");
        return;
      }

      // Load rows
      const rowsResult = await fetchRows(tableId);
      if (rowsResult.ok) {
        setRows(rowsResult.rows);
      }

      // Load aggregations
      const aggResult = await fetchAggregations(tableId);
      if (aggResult.ok) {
        setAggregations(aggResult.aggregations);
      }
    } catch (error) {
      console.error("Failed to load table data:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleReorderColumns = async (columnOrders) => {
    const result = await reorderColumns({
      table_id: tableId,
      column_orders: columnOrders,
    });
    if (result.ok) {
      window.showToast?.("컬럼 순서가 변경되었습니다.", "success");
      setRefreshKey((k) => k + 1);
      setShowReorderModal(false);
    } else if (result.denied) {
      window.showToast?.("컬럼 순서 변경 권한이 없습니다.", "error");
    } else {
      window.showToast?.("컬럼 순서 변경에 실패했습니다.", "error");
    }
  };

  const canEdit =
    permission === "owner" || permission === "admin" || permission === "edit";
  const canManage = permission === "owner" || permission === "admin";

  const handleAddColumn = async (data) => {
    const result = await addColumn({ table_id: tableId, ...data });
    if (result.ok) {
      window.showToast?.("컬럼이 추가되었습니다.", "success");
      setRefreshKey((k) => k + 1);
      setShowColumnModal(false);
    } else if (result.denied) {
      window.showToast?.("컬럼 추가 권한이 없습니다.", "error");
    } else {
      window.showToast?.("컬럼 추가에 실패했습니다.", "error");
    }
  };

  const handleUpdateColumn = async (data) => {
    const result = await updateColumn({
      table_id: tableId,
      column_id: editingColumn.id,
      ...data,
    });
    if (result.ok) {
      window.showToast?.("컬럼이 수정되었습니다.", "success");
      setRefreshKey((k) => k + 1);
      setEditingColumn(null);
    } else if (result.denied) {
      window.showToast?.("컬럼 수정 권한이 없습니다.", "error");
    } else {
      window.showToast?.("컬럼 수정에 실패했습니다.", "error");
    }
  };

  const handleDeleteColumn = async (columnId) => {
    if (
      !window.confirm(
        "정말로 이 컬럼을 삭제하시겠습니까? 관련된 모든 데이터가 삭제됩니다."
      )
    )
      return;

    const result = await deleteColumn({
      table_id: tableId,
      column_id: columnId,
    });
    if (result.ok) {
      window.showToast?.("컬럼이 삭제되었습니다.", "success");
      setRefreshKey((k) => k + 1);
    } else if (result.denied) {
      window.showToast?.("컬럼 삭제 권한이 없습니다.", "error");
    } else {
      window.showToast?.("컬럼 삭제에 실패했습니다.", "error");
    }
  };

  const handleAddRow = async (data) => {
    const result = await createRow({ table_id: tableId, row_data: data });
    if (result.ok) {
      window.showToast?.("데이터가 추가되었습니다.", "success");
      setRefreshKey((k) => k + 1);
      setShowRowModal(false);
    } else if (result.denied) {
      window.showToast?.("데이터 추가 권한이 없습니다.", "error");
    } else {
      window.showToast?.("데이터 추가에 실패했습니다.", "error");
    }
  };

  const handleUpdateRow = async (data) => {
    const result = await updateRow({
      table_id: tableId,
      row_id: editingRow.id,
      row_data: data,
    });
    if (result.ok) {
      window.showToast?.("데이터가 수정되었습니다.", "success");
      setRefreshKey((k) => k + 1);
      setEditingRow(null);
    } else if (result.denied) {
      window.showToast?.("데이터 수정 권한이 없습니다.", "error");
    } else {
      window.showToast?.("데이터 수정에 실패했습니다.", "error");
    }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm("정말로 이 데이터를 삭제하시겠습니까?")) return;

    const result = await deleteRow({ table_id: tableId, row_id: rowId });
    if (result.ok) {
      window.showToast?.("데이터가 삭제되었습니다.", "success");
      setRefreshKey((k) => k + 1);
    } else if (result.denied) {
      window.showToast?.("데이터 삭제 권한이 없습니다.", "error");
    } else {
      window.showToast?.("데이터 삭제에 실패했습니다.", "error");
    }
  };

  const handleSaveTemplate = async (data) => {
    const result = await createCustomTemplate({
      table_id: tableId,
      ...data,
    });

    if (result.denied) {
      window.showToast?.("템플릿 생성 권한이 없습니다.", "error");
    } else if (result.ok) {
      window.showToast?.("템플릿이 저장되었습니다.", "success");
      setShowSaveTemplateModal(false);
    } else {
      window.showToast?.("템플릿 저장에 실패했습니다.", "error");
    }
  };

  const handleExportCSV = () => {
    // Prepare CSV data
    const csvRows = [];

    // Filter out ID column
    const visibleColumns = columns.filter((col) => col.column_key !== "id");

    // Header row - add index column
    const headers = ["번호", ...visibleColumns.map((col) => col.display_name)];
    csvRows.push(headers);

    // Data rows with index
    filteredRows.forEach((row, index) => {
      const rowData = [
        index + 1, // 번호
        ...visibleColumns.map((col) => {
          const value = row.data[col.column_key];

          // Handle different data types
          if (value === null || value === undefined) return "";
          if (typeof value === "boolean") return value ? "예" : "아니오";
          if (col.data_type === "date" && value) {
            try {
              return new Date(value).toLocaleDateString("ko-KR");
            } catch {
              return value;
            }
          }
          if (col.data_type === "datetime" && value) {
            try {
              const date = new Date(value);
              return `${date.toLocaleDateString(
                "ko-KR"
              )} ${date.toLocaleTimeString("ko-KR", {
                hour: "2-digit",
                minute: "2-digit",
              })}`;
            } catch {
              return value;
            }
          }

          // Escape quotes and handle commas
          const stringValue = String(value);
          if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }),
      ];
      csvRows.push(rowData);
    });

    // Convert to CSV string with BOM for Excel
    const BOM = "\uFEFF";
    const csvContent = BOM + csvRows.map((row) => row.join(",")).join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const fileName = `${table.table_name}_${
      new Date().toISOString().split("T")[0]
    }.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.showToast?.(
      `${filteredRows.length}개 데이터를 다운로드했습니다.`,
      "success"
    );
  };

  // Filter rows
  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    return Object.values(row.data).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!table) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            테이블을 찾을 수 없습니다
          </h2>
          <button
            onClick={() => navigate("/financial_management")}
            className="text-blue-600 hover:text-blue-700"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/finance")}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>

                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {table.table_name}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {table.description}
                  </p>
                </div>

                <PermissionBadge type="table" level={permission} />
              </div>

              <div className="flex items-center gap-3">
                {canManage && (
                  <>
                    <button
                      onClick={() => setShowSaveTemplateModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        템플릿으로 저장
                      </span>
                    </button>

                    <button
                      onClick={() => setShowPermissions(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm font-medium">권한 관리</span>
                    </button>

                    <button
                      onClick={() => setShowShare(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="text-sm font-medium">공유</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="데이터 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <span>{statistics.row_count || 0}개 행</span>
                  <span>{statistics.column_count || 0}개 컬럼</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {canEdit && (
                  <>
                    <button
                      onClick={() => setShowReorderModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <ArrowUpDown className="w-4 h-4" />
                      <span className="text-sm font-medium">컬럼 순서</span>
                    </button>

                    <button
                      onClick={() => setShowColumnModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm font-medium">컬럼 추가</span>
                    </button>

                    <button
                      onClick={() => setShowRowModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm font-medium">데이터 추가</span>
                    </button>
                  </>
                )}

                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-medium">CSV 다운로드</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="max-w-7xl mx-auto px-6 pb-8">
          <DataTable
            columns={columns}
            rows={paginatedRows}
            aggregations={aggregations}
            permission={permission}
            onEditColumn={canManage ? setEditingColumn : null}
            onDeleteColumn={canManage ? handleDeleteColumn : null}
            onEditRow={canEdit ? setEditingRow : null}
            onDeleteRow={canEdit ? handleDeleteRow : null}
            onCreateAggregation={canManage ? createAggregation : null}
            onDeleteAggregation={canManage ? deleteAggregation : null}
            startIndex={(currentPage - 1) * rowsPerPage}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {(currentPage - 1) * rowsPerPage + 1} -{" "}
                {Math.min(currentPage * rowsPerPage, filteredRows.length)} /
                전체 {filteredRows.length}개
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showPermissions && (
          <TablePermissions
            isOpen={showPermissions}
            onClose={() => setShowPermissions(false)}
            tableId={tableId}
            tableName={table.table_name}
          />
        )}

        {showShare && (
          <ShareTableModal
            isOpen={showShare}
            onClose={() => setShowShare(false)}
            tableId={tableId}
            tableName={table.table_name}
            onUpdate={() => setRefreshKey((k) => k + 1)}
          />
        )}

        {showColumnModal && (
          <ColumnModal
            isOpen={showColumnModal}
            onClose={() => setShowColumnModal(false)}
            onSubmit={handleAddColumn}
            columns={columns}
          />
        )}

        {editingColumn && (
          <ColumnModal
            isOpen={!!editingColumn}
            onClose={() => setEditingColumn(null)}
            onSubmit={handleUpdateColumn}
            column={editingColumn}
            columns={columns}
          />
        )}

        {showRowModal && (
          <RowModal
            isOpen={showRowModal}
            onClose={() => setShowRowModal(false)}
            onSubmit={handleAddRow}
            columns={columns}
          />
        )}

        {editingRow && (
          <RowModal
            isOpen={!!editingRow}
            onClose={() => setEditingRow(null)}
            onSubmit={handleUpdateRow}
            columns={columns}
            row={editingRow}
          />
        )}

        {showSaveTemplateModal && (
          <SaveTemplateModal
            isOpen={showSaveTemplateModal}
            onClose={() => setShowSaveTemplateModal(false)}
            onSubmit={handleSaveTemplate}
            tableId={tableId}
            tableName={table.table_name}
          />
        )}

        {showReorderModal && (
          <ColumnReorderModal
            isOpen={showReorderModal}
            onClose={() => setShowReorderModal(false)}
            columns={columns}
            onSubmit={handleReorderColumns}
          />
        )}
      </div>
    </>
  );
}
